import AgentIcon from "../AgentIcon";
import AgentChatMessage from "./AgentChatMessage";
import UserChatMessage from "./UserChatMessage";
import type { ConnectionState } from "@app/config/agentWebSocket";
import type { AgentChatMode, AgentChatSettings } from "@app/state/types";
import type { ChatMessage } from "@lib/agent/types";
import Icon from "@lib/common/components/Icon/Icon";
import formatChatDate from "@lib/common/utils/formatChatDate";
import ChatBox from "@lib/experiment/agent/ExperimentDesignChatBox/ChatBox";
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Group,
  Menu,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  Transition
} from "@mantine/core";
import { useThrottledState } from "@mantine/hooks";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type AgentChatProps = {
  messages: ChatMessage[];
  isStreaming: boolean;
  connectionState: ConnectionState;
  send: (content: string) => void;
  stop: VoidFunction;
  /** True while the persisted message history is loading on first mount. */
  isHistoryLoading?: boolean;
  /** True when the history bootstrap query failed; renders an inline retry banner. */
  historyError?: boolean;
  disabledClear?: boolean;
  userFeedback?: ReactNode;
  onRetryHistory?: () => void;
  onClear: VoidFunction;
  variant?: "draft" | "experiment";
} & Pick<AgentChatSettings, "chatMode"> & {
    setChatMode: (mode: AgentChatMode) => void;
    toggle: (value: boolean) => void;
  };

export default function AgentChat({
  messages,
  isStreaming,
  connectionState,
  send,
  stop,
  isHistoryLoading = false,
  historyError = false,
  onRetryHistory,
  onClear,
  disabledClear,
  userFeedback,
  variant = "experiment",
  chatMode,
  setChatMode,
  toggle
}: AgentChatProps) {
  const isReady = connectionState === "connected";
  const isReconnecting = connectionState === "reconnecting";

  const viewportRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const [scrollPosition, setScrollPosition] = useThrottledState<{ x: number; y: number }>({ x: 0, y: 0 }, 500);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const scrollToBottom = useCallback(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTo({ top: viewportRef.current.scrollHeight, behavior: "smooth" });
    }
    // Re-focus chat input.
    chatInputRef.current?.focus();
  }, []);

  // Scroll to the bottom the first time messages populate so history loads at the end.
  const hasInitialScrolledRef = useRef(false);
  useEffect(() => {
    if (hasInitialScrolledRef.current || messages.length === 0) {
      return;
    }
    hasInitialScrolledRef.current = true;
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Auto-scroll on new messages or streaming content, but only when the user
  // is already near the bottom — otherwise scrolling up to re-read would be
  // yanked back by every streamed chunk.
  // useEffect(() => {
  //   const el = viewportRef.current;
  //   if (!el) {
  //     return;
  //   }
  //   const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
  //   if (nearBottom) {
  //     scrollToBottom();
  //   }
  // }, [messages, scrollToBottom]);

  // Toggle Scroll-to-bottom button on scroll change.
  useEffect(() => {
    if (viewportRef.current) {
      setShowScrollToBottom(
        viewportRef.current.scrollHeight - scrollPosition.y > viewportRef.current.clientHeight + 50
      );
    }
  }, [scrollPosition]);

  // Focus chat input when connected
  useEffect(() => {
    if (isReady) {
      chatInputRef.current?.focus();
      scrollToBottom();
    }
  }, [isReady, scrollToBottom]);

  const formattedDate = useMemo(() => formatChatDate(new Date()), []);

  const lastAssistantExtras = [...messages].reverse().find(m => m.role === "assistant" && m.extras)?.extras;
  const lastOpenQuestion =
    lastAssistantExtras && [...lastAssistantExtras].reverse().find(item => item.action === "question_open");
  const defaultPlaceholder = variant === "draft" ? "Describe your experiment..." : "Ask about this experiment...";
  const inputSuggestion =
    lastOpenQuestion && lastOpenQuestion.action === "question_open" ? lastOpenQuestion.input_placeholder : null;

  const disabled = useMemo(
    () => !isReady || isHistoryLoading || historyError,
    [isReady, isHistoryLoading, historyError]
  );
  const showEmptyState = !isHistoryLoading && !historyError && messages.length === 0;

  return (
    <Stack h="100%" gap={0}>
      <Group px="xs" align="center" gap="md" justify="space-between" pb={5} wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <AgentIcon size="1rem" />
          <Stack gap={0}>
            <Text size="xs" fw={500} lh={1} truncate>
              AI chat · {formattedDate}
            </Text>
            {isReconnecting && (
              <Text size="xs" c="orange" lh={1.4}>
                Reconnecting…
              </Text>
            )}
          </Stack>
        </Group>
        <Group gap={5} wrap="nowrap">
          <Tooltip label="Clear conversation" disabled={disabledClear}>
            <ActionIcon onClick={onClear} disabled={disabledClear}>
              <Icon name="trash" size="0.9rem" />
            </ActionIcon>
          </Tooltip>

          <Menu width={150}>
            <Menu.Target>
              <Tooltip label="Switch chat mode">
                <ActionIcon>
                  <Icon name="sidebar-flip" size="1rem" />
                </ActionIcon>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() => setChatMode("sidebar")}
                leftSection={<Icon name="sidebar-flip" color="dimmed" size="0.8rem" />}
                rightSection={chatMode === "sidebar" ? <Icon name="check" /> : undefined}
              >
                Sidebar
              </Menu.Item>
              <Menu.Item
                onClick={() => setChatMode("floating")}
                leftSection={<Icon name="window-restore" color="dimmed" size="0.8rem" />}
                rightSection={chatMode === "floating" ? <Icon name="check" /> : undefined}
              >
                Floating
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <Tooltip label="Hide chat">
            <ActionIcon onClick={() => toggle(false)}>
              <Icon name="dash" size="0.8rem" variant="solid" />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      <ScrollArea viewportRef={viewportRef} onScrollPositionChange={setScrollPosition} flex={1} scrollbarSize={2}>
        <Box p="sm" pb="5rem" pr="sm">
          <Stack gap="md" py="md" style={{ display: "flex", flexDirection: "column" }}>
            {historyError && (
              <Center>
                <Stack gap="sm" align="center" mt="lg" maw={400}>
                  <Text c="dimmed" ta="center">
                    Couldn&apos;t load the conversation history.
                  </Text>
                  {onRetryHistory && (
                    <Button variant="light" size="xs" onClick={onRetryHistory}>
                      Retry
                    </Button>
                  )}
                </Stack>
              </Center>
            )}
            {isHistoryLoading && (
              <Stack gap="md" py="md">
                <Skeleton height={48} width="60%" radius="md" />
                <Skeleton height={64} width="80%" radius="md" ml="auto" />
                <Skeleton height={48} width="55%" radius="md" />
              </Stack>
            )}
            {showEmptyState && (
              <Center>
                <Box mt="20vh" maw={650}>
                  <Stack gap="md">
                    {variant === "draft" ? (
                      <Stack gap="sm">
                        <Text style={{ textWrap: "balance" }}>
                          Describe your experiment and I&apos;ll fill in the draft next to this chat.
                          <br />
                          <br />
                          You can also edit any field by hand — we stay in sync.
                          <br />
                          <br />
                          Click <b>Create experiment</b> when you&apos;re ready.
                        </Text>
                      </Stack>
                    ) : (
                      <Stack gap="sm">
                        <Text style={{ textWrap: "balance" }}>
                          Ask about this experiment&apos;s recommendations, insights, status, expert context, etc.
                          <br />
                          <br />I can also help you reconfigure the experiment, like updating the expert context,
                          modifying variables and results, adjusting parallelization, or phrase a good description.
                        </Text>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              </Center>
            )}
            {messages.map((message, i) =>
              message.role === "user" ? (
                <UserChatMessage message={message} key={message.id} />
              ) : (
                <AgentChatMessage
                  key={message.id}
                  message={message}
                  isStreaming={isStreaming && i === messages.length - 1}
                  onSend={send}
                />
              )
            )}
          </Stack>
        </Box>
      </ScrollArea>
      {userFeedback ? <Center mb={5}>{userFeedback}</Center> : undefined}
      <Box px="xs" pos="relative">
        <Transition mounted={showScrollToBottom} transition="pop" enterDelay={200}>
          {transitionStyles => (
            <Center pos="absolute" left={0} right={0} top={-60}>
              <ActionIcon
                style={{
                  ...transitionStyles,
                  boxShadow: "var(--mantine-shadow-lg)",
                  border: "solid 1px var(--mantine-color-default-border)"
                }}
                radius="xl"
                onClick={scrollToBottom}
                bg="var(--mantine-color-default)"
              >
                <Icon name="arrow-down" size="0.8rem" color="dimmed" />
              </ActionIcon>
            </Center>
          )}
        </Transition>

        <ChatBox
          ref={chatInputRef}
          onSubmit={send}
          onCancel={stop}
          disabled={disabled}
          isStreaming={isStreaming}
          inputSuggestion={inputSuggestion}
          textareaProps={{
            placeholder: defaultPlaceholder,
            minRows: 2,
            maxRows: 6
          }}
        />
        <Center>
          <Group gap="xs" mt={7} align="center">
            <Text c="dimmed" size="xs">
              AI can make mistakes. Verify important information.
            </Text>
          </Group>
        </Center>
      </Box>
    </Stack>
  );
}
